import type { ReactNode } from "react";

interface DocumentDesignOverridePanelProps {
  title: string;
  description?: string;
  isOverriding: boolean;
  onToggle: (next: boolean) => void;
  /**
   * Read-only preview of the tenant default value, shown when
   * `isOverriding` is false. Helps the user see what they'd be
   * overriding before they flip the switch.
   */
  defaultPreview?: ReactNode;
  /** Editor surface, shown only when `isOverriding` is true. */
  children: ReactNode;
}

export function DocumentDesignOverridePanel({
  title,
  description,
  isOverriding,
  onToggle,
  defaultPreview,
  children,
}: DocumentDesignOverridePanelProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-[20px] font-medium tracking-tight">
          {title}
        </h3>
        {description && (
          <p className="text-[12.5px] text-ink-3 mt-1">{description}</p>
        )}
      </div>

      <div className="bg-paper-hi rounded-lg shadow-card p-4">
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <span
            className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
              isOverriding ? "bg-tide" : "bg-ink-5"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-paper-hi transition-transform shadow-sm ${
                isOverriding ? "translate-x-4" : ""
              }`}
            />
            <input
              type="checkbox"
              className="sr-only"
              checked={isOverriding}
              onChange={(e) => onToggle(e.target.checked)}
              aria-label={`Override ${title.toLowerCase()}`}
            />
          </span>
          <span className="text-[13px] font-medium">
            {isOverriding ? "Overriding tenant default" : "Inherits from defaults"}
          </span>
        </label>

        <div className="mt-4">
          {isOverriding ? (
            children
          ) : (
            <div className="hair border rounded-md bg-canvas/30 p-4">
              <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-semibold mb-2">
                Tenant default
              </div>
              {defaultPreview ?? (
                <div className="text-[12.5px] text-ink-5 italic">
                  No default set.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
